// src/components/Codebook/CodebookCodeBlock.tsx
// Code block with syntax highlighting and execution

import React, { useState, useEffect } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { analyticsApi } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';

interface CodebookCodeBlockProps {
  language: string;
  code: string;
  explanation?: string;
  codebookId?: string;
  blockIndex?: number;
  readOnly?: boolean;
  // SQL blocks only: hidden schema+seed script executed before the visible SQL
  setupSql?: string;
}

const CodebookCodeBlock: React.FC<CodebookCodeBlockProps> = ({
  language,
  code,
  explanation,
  codebookId,
  blockIndex,
  readOnly = false,
  setupSql
}) => {
  // oneDark is a dark-background theme; its light-gray base text washes out on light cards
  const { theme } = useTheme();
  const syntaxStyle = theme === 'dark' ? oneDark : oneLight;
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<{
    output?: string;
    error?: string;
    success?: boolean;
    validation_errors?: string[];
    generated_files?: Array<{ filename: string; image_base64?: string; image_mime?: string; size: number }>;
  } | null>(null);
  const [showOutput, setShowOutput] = useState(false);
  const [showSchema, setShowSchema] = useState(false);

  // Load last execution result when component mounts (skip in readOnly mode)
  useEffect(() => {
    if (!readOnly && codebookId && blockIndex !== undefined) {
      loadLastExecution();
    }
  }, [codebookId, blockIndex, readOnly]);

  const loadLastExecution = async () => {
    if (!codebookId || blockIndex === undefined) return;

    try {
      console.log('[CodebookCodeBlock] Loading last execution for block', blockIndex);

      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/codebooks/${codebookId}/executions/latest?block_index=${blockIndex}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.execution) {
          console.log('[CodebookCodeBlock] Loaded last execution:', data.execution);
          // Extract generated_files from execution_details if available
          const details = data.execution.execution_details || {};
          const savedFiles = details.generated_files || [];
          setExecutionResult({
            output: data.execution.stdout || '',
            error: data.execution.stderr || data.execution.error_message || '',
            success: data.execution.success ?? true,
            validation_errors: [],
            generated_files: savedFiles
          });
          setShowOutput(true);
        }
      }
    } catch (err) {
      console.error('[CodebookCodeBlock] Error loading last execution:', err);
      // Silently fail - execution history is optional
    }
  };

  const handleExecuteCode = async () => {
    setIsExecuting(true);
    setShowOutput(true);
    setExecutionResult(null);

    try {
      // Get session_id from URL or generate a unique one for this execution
      const urlParams = new URLSearchParams(window.location.search);
      const session_id = urlParams.get('session_id') || `codebook_${Date.now()}`;

      console.log('[CodebookCodeBlock] Executing code with params:', {
        language,
        code_length: code.length,
        session_id,
        codebookId,
        blockIndex
      });

      // Call /execute-code endpoint with full parameters (same as main chat route)
      const result = await analyticsApi.executeCode({
        code,
        language,
        session_id,  // Pass session_id for Service Bus topics routing
        ...(setupSql ? { setup_sql: setupSql } : {})
      });

      console.log('[CodebookCodeBlock] Execution result:', {
        success: result.success,
        executor: result.executor,
        output_length: (result.output || result.stdout || '').length,
        has_validation_errors: !!result.validation_errors
      });

      const executionResult = {
        output: result.output || result.stdout || '',
        error: result.error || result.stderr || '',
        success: result.success ?? true,
        validation_errors: result.validation_errors,
        generated_files: result.generated_files || []
      };

      setExecutionResult(executionResult);

      // Save execution result to backend for persistence
      if (codebookId && blockIndex !== undefined) {
        try {
          console.log('[CodebookCodeBlock] 🔍 Saving execution for codebook:', codebookId, 'block:', blockIndex);

          const saveUrl = `/api/codebooks/${codebookId}/blocks/${blockIndex}/execute`;
          const savePayload = {
            language,
            code,
            execution_result: {
              success: result.success ?? true,
              stdout: result.output || result.stdout || '',
              stderr: result.error || result.stderr || '',
              execution_time: result.execution_time || 0,
              executor: result.executor || 'unknown',
              error: result.error || '',
              generated_files: result.generated_files || []
            }
          };

          console.log('[CodebookCodeBlock] 📤 POST', saveUrl);
          const saveToken = localStorage.getItem('token');
          const saveResponse = await fetch(saveUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(saveToken ? { 'Authorization': `Bearer ${saveToken}` } : {})
            },
            body: JSON.stringify(savePayload)
          });

          console.log('[CodebookCodeBlock] 📨 Response status:', saveResponse.status);

          if (!saveResponse.ok) {
            const errorData = await saveResponse.json().catch(() => ({}));
            console.error('[CodebookCodeBlock] ❌ Failed to save execution:', saveResponse.status, errorData);
          } else {
            const successData = await saveResponse.json();
            console.log('[CodebookCodeBlock] ✅ Execution saved successfully:', successData.execution_id);
          }
        } catch (saveErr: any) {
          console.error('[CodebookCodeBlock] ❌ Exception during save:', saveErr.message);
          // Don't block execution display if save fails
        }
      } else {
        console.warn('[CodebookCodeBlock] ⚠️  Cannot save: codebookId=', codebookId, 'blockIndex=', blockIndex);
      }
    } catch (err: any) {
      console.error('[CodebookCodeBlock] Execution error:', err);
      console.error('[CodebookCodeBlock] Error response:', err.response?.data);
      setExecutionResult({
        error: err.response?.data?.detail || err.message || 'Code execution failed',
        success: false
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const copyBtnId = `copy-${codebookId}-${blockIndex}`;
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      // Brief visual feedback
      const btn = document.querySelector(`[data-copy-btn="${copyBtnId}"]`) as HTMLButtonElement;
      if (btn) {
        const originalText = btn.textContent;
        btn.textContent = 'Copied!';
        setTimeout(() => {
          btn.textContent = originalText;
        }, 2000);
      }
    } catch (err) {
      console.error('[CodebookCodeBlock] Copy error:', err);
    }
  };

  return (
    <div className="space-y-4">
      {/* Code block with premium border effect. border-white is invisible on
          the light theme's white page — use the theme token there (issue #138) */}
      <div className={`relative rounded-lg border-2 ${theme === 'dark' ? 'border-white' : 'border-border'} overflow-hidden`}>
        {/* Purple gradient accent at top */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>

        {/* Code block header */}
        <div className={`flex items-center justify-between bg-card px-4 py-2 border-b ${theme === 'dark' ? 'border-white/10' : 'border-border'}`}>
        <span className="text-sm font-mono text-muted-foreground">{language.toUpperCase()}</span>
        <div className="flex gap-2">
          <button
            onClick={handleCopyCode}
            data-copy-btn={copyBtnId}
            className="px-3 py-1 text-xs bg-background hover:bg-card border border-border rounded transition-colors hover:text-primary"
          >
            Copy
          </button>
          {setupSql && (
            <button
              onClick={() => setShowSchema(!showSchema)}
              className="px-3 py-1 text-xs bg-background hover:bg-card border border-border rounded transition-colors hover:text-primary"
            >
              {showSchema ? 'Hide schema' : 'View schema'}
            </button>
          )}
          {!readOnly && ['python', 'javascript', 'java', 'cpp', 'c', 'go', 'rust', 'ruby', 'sql'].includes(language.toLowerCase()) && (
            <button
              onClick={handleExecuteCode}
              disabled={isExecuting}
              className="px-3 py-1 text-xs bg-primary hover:bg-primary/90 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {isExecuting ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                  Running...
                </>
              ) : (
                <>
                  ▶ Run
                </>
              )}
            </button>
          )}
        </div>
      </div>

        {/* SQL blocks: collapsible schema + seed data the query runs against */}
        {setupSql && showSchema && (
          <div className={`relative bg-card/70 overflow-auto max-h-[300px] border-b ${theme === 'dark' ? 'border-white/10' : 'border-border'}`}>
            <div className="px-4 pt-2 text-xs text-muted-foreground">
              Schema &amp; sample data (runs automatically before this query)
            </div>
            <SyntaxHighlighter
              language="sql"
              style={syntaxStyle}
              codeTagProps={{ style: { background: 'transparent' } }}
              customStyle={{
                margin: 0,
                padding: '1rem',
                fontSize: '0.8rem',
                lineHeight: '1.5',
                backgroundColor: 'transparent',
                opacity: 0.85
              }}
            >
              {setupSql}
            </SyntaxHighlighter>
          </div>
        )}

        {/* Code block with syntax highlighting */}
        <div className="relative bg-card overflow-auto min-h-[100px] max-h-[500px]">
          <SyntaxHighlighter
            language={language}
            style={syntaxStyle}
            codeTagProps={{ style: { background: 'transparent' } }}
            customStyle={{
              margin: 0,
              padding: '1rem',
              fontSize: '0.875rem',
              lineHeight: '1.5',
              backgroundColor: 'transparent'
            }}
          >
            {code}
          </SyntaxHighlighter>
        </div>

        {/* Execution output */}
        {showOutput && executionResult && (
        <div className={`border-t ${theme === 'dark' ? 'border-white/10' : 'border-border'} p-4 ${executionResult.success ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
          {executionResult.validation_errors && executionResult.validation_errors.length > 0 ? (
            <>
              <h4 className="font-semibold text-sm mb-2 text-red-600">Syntax Errors</h4>
              <div className="space-y-2">
                {executionResult.validation_errors.map((error, idx) => (
                  <div key={idx} className="text-sm font-mono bg-red-500/5 border border-red-500/20 rounded px-3 py-2 text-foreground">
                    {error}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <h4 className="font-semibold text-sm mb-2">
                {executionResult.success ? 'Output' : 'Error'}
              </h4>
              {/* Inline image preview */}
              {executionResult.success && executionResult.generated_files?.some(f => f.image_base64) && (
                <div className="mb-3 space-y-2">
                  {executionResult.generated_files!.filter(f => f.image_base64).map((file, imgIdx) => (
                    <div key={imgIdx} className="border border-border rounded-lg overflow-hidden bg-[#1a1a2e] flex flex-col items-center">
                      <div className="p-4 flex items-center justify-center w-full">
                        <img
                          src={`data:${file.image_mime || 'image/png'};base64,${file.image_base64}`}
                          alt={file.filename}
                          className="max-w-full max-h-[500px] object-contain rounded"
                        />
                      </div>
                      <div className="px-3 py-1.5 bg-muted/50 text-xs text-muted-foreground flex justify-between items-center w-full">
                        <span>{file.filename}</span>
                        <span>{(file.size / 1024).toFixed(1)} KB</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <pre className="text-sm text-foreground whitespace-pre-wrap break-words font-mono">
                {executionResult.success ? executionResult.output : executionResult.error}
              </pre>
            </>
          )}
        </div>
      )}
      </div>

      {/* Explanation */}
      {explanation && (
        <div className="bg-card/50 border border-border rounded-lg p-4">
          <p className="text-sm text-muted-foreground italic">{explanation}</p>
        </div>
      )}
    </div>
  );
};

export default CodebookCodeBlock;
